/**
 * Freya — the Asian Trails assistant, inside Amassa.
 *
 * Feature-local API surface, deliberately NOT added to `coportal.api.ts`
 * (5,900 lines and a standing merge-conflict hotspot).
 *
 * The routes kept their old `chatbot` path so bookmarks and the rail link
 * survive the Roux → Freya swap. Amassa is only a bridge: it mints a short
 * token for the signed-in user and forwards the turn to Freya, which does the
 * thinking. Anything that would WRITE comes back as a `pending` item the
 * operator confirms or cancels here.
 *
 *   GET  /api/coportal/chatbot/status          · configured + name + avatar
 *   POST /api/coportal/chatbot/message         · one turn → reply_html + pending
 *   POST /api/coportal/chatbot/confirm         · approve / cancel one pending
 *   POST /api/coportal/chatbot/reset           · forget the conversation
 *   GET  /api/coportal/chatbot/threads/{id}    · replay a conversation
 *
 * Every `*_html` field is server text: render it through `SafeHtml`, never
 * as raw markup.
 */
import { apiFetchRaw } from '@/auth/api';

export const FREYA_FALLBACK_NAME = 'Freya';

export interface FreyaStatus {
  /** Freya reachable AND the caller holds `bot.access`. */
  configured: boolean;
  /** `chatbot_enabled` — the ops-team kill switch. */
  enabled: boolean;
  /** The caller holds `bot.access` (Admin ▸ Users ▸ Freya rights). */
  hasAccess: boolean;
  name: string;
  /** Empty string means fall back to the bot glyph. */
  avatar: string;
  note: string;
}

export async function fetchFreyaStatus(signal?: AbortSignal): Promise<FreyaStatus> {
  const res = await apiFetchRaw<{
    ok?: boolean;
    configured?: boolean;
    enabled?: boolean;
    has_access?: boolean;
    name?: string;
    avatar?: string;
    note?: string;
  }>('/coportal/chatbot/status', { method: 'GET', signal });

  // A leftover "Roux" in `chatbot_name` is the old engine's name, not a
  // deliberate rebrand, so it reads as Freya.
  const raw = (res?.name ?? '').trim();
  return {
    configured: res?.configured === true,
    enabled: res?.enabled === true,
    hasAccess: res?.has_access === true,
    name: !raw || /^roux$/i.test(raw) ? FREYA_FALLBACK_NAME : raw,
    avatar: (res?.avatar ?? '').trim(),
    note: res?.note ?? '',
  };
}

/** Something Freya wants to write, waiting for the operator's say-so. */
export interface FreyaPending {
  id: string;
  previewHtml: string;
  /** ISO timestamp; empty when the server sent none. */
  expiresAt: string;
}

export interface FreyaReply {
  html: string;
  pending: FreyaPending[];
  threadId: string | null;
}

interface RawPending {
  id?: string | number;
  preview_html?: string;
  expires_at?: string;
}

function toPending(list: RawPending[] | undefined): FreyaPending[] {
  return (list ?? [])
    .filter((p) => p && p.id !== undefined && p.id !== null && String(p.id) !== '')
    .map((p) => ({
      id: String(p.id),
      previewHtml: p.preview_html ?? '',
      expiresAt: p.expires_at ?? '',
    }));
}

export async function sendFreyaMessage(
  message: string,
  threadId: string | null,
  signal?: AbortSignal,
): Promise<FreyaReply> {
  const res = await apiFetchRaw<{
    ok?: boolean;
    reply_html?: string;
    pending?: RawPending[];
    thread_id?: string | null;
    error?: string;
  }>(
    '/coportal/chatbot/message',
    {
      method: 'POST',
      body: JSON.stringify({ message, thread_id: threadId }),
      signal,
    },
    { mutation: true },
  );

  if (res?.ok === false) throw new Error(res.error || 'Freya could not answer.');
  return {
    html: res?.reply_html ?? '',
    pending: toPending(res?.pending),
    threadId: res?.thread_id ?? threadId,
  };
}

/**
 * `threadId` is optional: when given, the server also appends the result to
 * that conversation (only if the thread is the caller's own).
 */
export async function confirmFreya(
  id: string,
  approve: boolean,
  threadId?: string | null,
): Promise<string> {
  const res = await apiFetchRaw<{ ok?: boolean; result_html?: string; error?: string }>(
    '/coportal/chatbot/confirm',
    { method: 'POST', body: JSON.stringify({ id, approve, thread_id: threadId ?? null }) },
    { mutation: true },
  );
  if (res?.ok === false) throw new Error(res.error || 'Freya could not finish that.');
  return res?.result_html ?? '';
}

/** Best effort: a failed reset must not block starting a new conversation. */
export async function resetFreya(): Promise<void> {
  try {
    await apiFetchRaw('/coportal/chatbot/reset', { method: 'POST', body: '{}' }, { mutation: true });
  } catch {
    /* ignore */
  }
}

export interface FreyaTurn {
  role: 'user' | 'assistant';
  /** Plain text for the user, sanitisable HTML for Freya. */
  content: string;
  pending?: FreyaPending[];
}

export async function fetchFreyaThread(
  threadId: string,
  signal?: AbortSignal,
): Promise<FreyaTurn[]> {
  try {
    const res = await apiFetchRaw<{
      ok?: boolean;
      messages?: Array<{ role?: string; content?: string }>;
    }>(`/coportal/chatbot/threads/${encodeURIComponent(threadId)}`, {
      method: 'GET',
      signal,
    });
    return (res?.messages ?? [])
      .filter((m): m is { role: string; content: string } =>
        (m.role === 'user' || m.role === 'assistant') && !!m.content)
      .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));
  } catch {
    return [];
  }
}
